import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { formatCurrency } from "../../../utils/currencyUtils";
import { AccountStatsDto } from "../../../shared/dtos/account/AccountStatsDto";

const COLORS = ["#4caf50", "#f44336", "#2196f3", "#ffc107", "#ff5722"];

interface AccountWidgetProps {
  data: AccountStatsDto;
}

const AccountWidget: React.FC<AccountWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("ACCOUNT");
  const Icon = meta.icon;
  const theme = useTheme();

  const chartData = [
    { name: "Assets", value: data.totalAssetBalance },
    { name: "Liabilities", value: data.totalLiabilityBalance },
    { name: "Equity", value: data.totalEquityBalance },
    { name: "Revenue", value: data.totalRevenueBalance },
    { name: "Expense", value: data.totalExpenseBalance },
  ];

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Active Accounts:{" "}
              <Box component="b" sx={{ fontWeight: "bold" }}>
                {data.totalActiveAccounts}
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ flexGrow: 1, width: "100%", minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              />
              <YAxis
                tickFormatter={(value: number) =>
                  new Intl.NumberFormat("en-US", {
                    notation: "compact",
                    compactDisplay: "short",
                  }).format(value)
                }
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              />
              <Tooltip
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
                formatter={(value) => formatCurrency(Number(value))}
              />
              <Bar dataKey="value">
                {chartData.map((_entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default AccountWidget;
